/**
 * Chart Session Separator Boundary Detection.
 *
 * Identifies timestamp transitions across trading session days (IST midnight boundaries)
 * over visible candle ranges for vertical session divider rendering.
 */

export const DEFAULT_SESSION_SEPARATOR_STYLE = {
  strokeStyle: "rgba(120,123,134,0.18)",
  lineWidth: 1,
} as const;

export interface LogicalRange {
  from: number;
  to: number;
}

/**
 * Finds all candle timestamps that mark the first candle of a new trading day
 * within the given visible logical index range.
 */
export function findSessionSeparatorTimes(
  allCandles: Array<{ time: number }> | null | undefined,
  range?: LogicalRange | null,
): number[] {
  if (!allCandles || allCandles.length <= 1) return [];

  const from = range ? Math.max(1, Math.floor(range.from)) : 1;
  const to = range
    ? Math.min(allCandles.length - 1, Math.ceil(range.to))
    : allCandles.length - 1;

  const times: number[] = [];
  for (let i = from; i <= to; i++) {
    const prev = allCandles[i - 1];
    const cur = allCandles[i];
    if (!prev || !cur) continue;

    // Day transition in +IST_OFFSET shifted epoch seconds (86,400s per day)
    if (Math.floor(cur.time / 86400) !== Math.floor(prev.time / 86400)) {
      times.push(cur.time);
    }
  }

  return times;
}
